import { type FC, type ReactNode, useCallback, useEffect, useLayoutEffect, useState } from "react";
import DesignationDisclaimer from "~/features/configurator/Linear/components/DesignationDisclaimer";
import { Tab } from "~/common/mui/Tabs/Tab";
import { Tabs } from "~/common/mui/Tabs/Tabs";
import { useDispatch, useSelector } from "react-redux";
import { XLIFE_LOGO_URL } from "~/features/configurator/Linear/constants";
import {
  useLinearPrice,
  userCanPurchaseLinear,
} from "~/features/configurator/Linear/components/Substeps/useLinearPrice";
import { Permission, userHasAnyPermission, userHasPermission } from "~/domains/user/utils/permissions";
import { userIsCustomer, userIsPublic, userIsRegistered } from "~/domains/user/utils/roles";
import NullableImage from "~/common/components/NullableImage";
import ProductDesignation from "~/features/productDetails/components/ProductDesignation";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import { Button, Stack } from "~/common/mui";
import { Card } from "~/features/configurator/Linear/components/Card";
import CircleSpinner from "~/common/components/CircleSpinner/CircleSpinner";
import * as PriceUtils from "~/utils/PriceUtils";
import { QuantityInput } from "~/common/components/QuantityInput/QuantityInput";
import { MAX_QUANTITY } from "~/utils/constants";
import {
  setLCSummaryTabAction,
  updatePriceReference,
  updateQuantity,
} from "~/features/configurator/Linear/redux/actions";
import StockIndicator from "~/domains/product/components/StockIndicator/StockIndicator";

import { IconType } from "~/common/components/icon/IconType";
import { ArrowLink } from "~/common/components/Link";
import LinearATCPopup from "~/features/configurator/common/components/Navigation/LinearATCPopup";
import LinearRequestQuotePopup from "~/features/configurator/Linear/components/LinearRequestQuotePopup";
import { LinearCad } from "~/features/configurator/Linear/components/LinearCad";
import { type RootState } from "~/app/store";
import { useSaveConfigPopup } from "~/features/configurator/Linear/hooks/useSaveConfigPopup";
import { CART_PAGE, REGISTER_PAGE, URL_CONTACT_US } from "~/utils/url/staticUrls";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import {
  getActiveTab,
  selectIsConfiguratorAddedToCartAlready,
  selectIsConfiguratorAddToCartEnabled,
} from "~/features/configurator/Linear/redux/selectors";
import { getRedirectToRegisterPage, redirectOrReload, redirectToUrl } from "~/utils/url/urlUtils";
import { getNetPrice, makeDescriptorFromFullState } from "~/domains/product/utils/productDescriptorUtils";
import { useTrackLCSimpleButtonClick } from "~/features/configurator/Linear/hooks/useTrackLCClicks";
import { useRequestOfferPopup } from "~/features/configurator/Linear/hooks/useRequestOfferPopup";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemEditableParams";
import { canSeePriceRefColumn } from "~/features/quickEntry/utils/helpers";
import { userCanSeeEndCustomer } from "~/features/cart/cart/components/LineItemStaticParams";
import { useTranslation } from "react-i18next";
import { useLinearAvailability } from "~/features/configurator/Linear/components/Substeps/useLinearAvailability";
import { LcSummaryTab } from "~/features/configurator/common/LcSummaryTab";
import { getConfiguratorDefinition } from "~/bridges/configurator/configuratorRegistry";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { useLinearProductInfo } from "~/features/configurator/Linear/redux/useLinearProductInfo";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/mui/Alert/Alert";
import type { LinearSubstep } from "~/features/configurator/Linear/models";
import { UNIT_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { useConfiguratorPageCustomization } from "./ConfiguratorPageCustomizationContext";
import DownloadIcon from "@mui/icons-material/Download";
import { generateLCDatasheetAction } from "../redux/actions";

export const SCROLL_TO_LC_SUMMARY_TABS = () => document.getElementById("Tab")?.scrollIntoView();

export const ConfigurationSummarySubstep = ({ def }: { def: LinearSubstep }) => {
  const { t } = useTranslation();
  const xmlT = useXmlConfiguratorTranslations().t;
  const productCode = useSelector(selectConfiguratorProductCode);
  const activeTab = useSelector(getActiveTab);

  const SummaryData = getConfiguratorDefinition(productCode).summary;
  const SummaryParams = getConfiguratorDefinition(productCode).summaryParams;

  const dispatch = useDispatch();

  const updateActiveTab = useCallback((tab: LcSummaryTab) => dispatch(setLCSummaryTabAction(tab)), []);

  const linearConfiguration = useSelector(getLinearConfiguration);
  const quantity = useSelector(getQuantity);
  const manufacturerLogoUrl = useSelector(getManufacturerLogo);
  const brandLogoUrl = XLIFE_LOGO_URL;

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isRequestQuoteOpen, setIsRequestQuoteOpen] = useState<boolean>(false);

  let imageUrl = useSelector(selectProductImageUrl);
  if (xmlT) {
    imageUrl = xmlT(imageUrl);
  }

  const openCloseModal = () => setIsModalOpen((prev) => !prev);
  const openCloseRequestQuote = () => setIsRequestQuoteOpen((prev) => !prev);

  const canPurchase = userCanPurchaseLinear();
  const canAddToCart = useSelector(selectIsConfiguratorAddToCartEnabled);
  const showAddToCart = userHasAnyPermission(Permission.ADD_TO_CART, Permission.ADD_TO_QUOTE);
  const canSeePrice = userHasPermission(Permission.PRICE);
  const canImportSave = userHasPermission(Permission.LINEAR_IMPORT);
  const isCustomer = userIsCustomer();
  const isRegistered = userIsRegistered();
  const isLatestKmatUser = userHasPermission(Permission.LINEAR_PURCHASE);
  const priceReference = useSelector(selectedPriceReference);

  const { showSave, showRequestOffer, embedded, forceShowCAD } = useConfiguratorPageCustomization();
  const showDownloadDatasheet = true;

  const { priceInfo, errorLoadingPrice, isLoadingPrice } = useLinearPrice({
    priceReference: priceReference,
  });

  const { leadTime, isLoadingAvailability, errorLoadingAvailability } = useLinearAvailability();
  const canSeeAvailability = userHasPermission(Permission.AVAILABILITY);

  let leadTimeElement = null;
  if (canSeeAvailability) {
    if (isLoadingAvailability) {
      leadTimeElement = <CircleSpinner />;
    } else if (errorLoadingAvailability || !leadTime) {
      leadTimeElement = "-";
    } else {
      leadTimeElement = (
        <StockIndicator level={2} changeText={t("linearConfigSummary.card.avaliability", { count: leadTime })} />
      );
    }
  }

  const productInfo = useLinearProductInfo();
  const productDescriptor = makeDescriptorFromFullState(
    productInfo,
    { state: isLoadingPrice ? "loading" : "loaded", data: priceInfo ? [priceInfo] : [] },
    undefined,
    priceReference,
  );
  const price = getNetPrice({
    totalQuantity: quantity,
    packCode: "",
    product: productDescriptor,
  });

  const isPublicUser = userIsPublic();

  const trackCADTabClick = useTrackLCSimpleButtonClick("open_CAD_tab", {
    product_name: linearConfiguration.designation,
  });

  useEffect(() => {
    if (activeTab === LcSummaryTab.CAD) {
      SCROLL_TO_LC_SUMMARY_TABS();
    }
  }, []);

  const isAddedToCartAlready = useSelector(selectIsConfiguratorAddedToCartAlready);

  const valuesPriceReference = window.SCHA.config.priceReferences;
  const webservicesDisabled = window.SCHA.config.disableAllWS;
  if (userCanSeeEndCustomer()) {
    if (!priceReference && valuesPriceReference?.[1]?.value) {
      dispatch(updatePriceReference(valuesPriceReference[1].value));
    }
  }

  const buttons = [];

  if (isPublicUser || isRegistered) {
    if (!embedded) {
      buttons.push(
        <RequestOfferButton key="request-offer" isPublicUser={isPublicUser}>
          {t("linearConfigSummary.card.requestOffer")}
        </RequestOfferButton>,
      );
    }
    if (showSave) {
      buttons.push(
        <SaveConfigurationButton key="save-config" isPublicUser={isPublicUser} canImportSave={canImportSave}>
          {t("linearConfigSummary.card.saveConfig")}
        </SaveConfigurationButton>,
      );
    }
    if (showDownloadDatasheet) {
      buttons.push(
        <Button
          key="download-datasheet"
          onClick={() => {
            dispatch(generateLCDatasheetAction());
          }}
          variant="text"
          startIcon={<DownloadIcon />}
          data-test-id={"linear-config-summary-download-datasheet-btn"}
          fullWidth={true}
          disabled={webservicesDisabled}
        >
          Download datasheet
          {/* {t("linearConfigSummary.card.downloadDatasheet")} */}
        </Button>,
      );
    }
  } else if (isCustomer) {
    if (showAddToCart && !isAddedToCartAlready) {
      buttons.push(
        <Button
          key="add-to-cart"
          startIcon={<DefaultButtonIcon type={IconType.Cart} />}
          data-test-id={"linear-config-summary-add-to-cart-btn"}
          fullWidth={true}
          disabled={!canAddToCart || webservicesDisabled}
          onClick={canPurchase ? openCloseModal : openCloseRequestQuote}
        >
          {t(`linearConfigSummary.card.${canPurchase ? "addToCart" : "addToQuote"}`)}
        </Button>,
      );
    }
    if (showAddToCart && isAddedToCartAlready) {
      buttons.push(
        <Button
          key="go-to-cart"
          startIcon={<DefaultButtonIcon type={IconType.Cart} />}
          data-test-id={"linear-config-summary-go-to-cart-btn"}
          fullWidth={true}
          href={CART_PAGE}
          disabled={webservicesDisabled}
        >
          {t(`page.linear.addToCart.button.goToCart`)}
        </Button>,
      );
    }
  }
  buttons.push(
    <ArrowLink key="contact-us" href={URL_CONTACT_US}>
      {t("linearConfigSummary.card.contactSpecialist")}
    </ArrowLink>,
  );
  const buttonsElement = buttons.length > 0 ? <Stack gap={1}>{buttons}</Stack> : null;

  return (
    <>
      <div>
        <div className={"scha__pdp pt-2"}>
          <div className={"row mb-4"}>
            <div className={"col-4 pt-30"}>
              <div className="row d-flex">
                <div className="col-9">
                  <NullableImage
                    data={{
                      url: imageUrl,
                      altText: "linear configuration product",
                    }}
                  />
                </div>
                <div className="col-3 d-flex flex-direction-column flex-justify-between">
                  <div>
                    {manufacturerLogoUrl && (
                      <img className={"scha__pdp__manufacturer-logo"} src={manufacturerLogoUrl} />
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={"col-push-1 col-3 pt-30"}>
              <ProductDesignation
                designation={linearConfiguration.designation}
                size={"headline-medium"}
                isPreferred={false}
              />
              <p className="scha__pdp__long-description">
                <LanguageAwareExternalContent
                  content={linearConfiguration.description}
                  className={"scha__lc__external-label"}
                />
              </p>
              <Stack className={"my-30"} direction="column" gap={2.5}>
                {SummaryParams && <SummaryParams />}

                {(isPublicUser || isRegistered) && buttonsElement}
              </Stack>
            </div>
            <div className={"col-4"}>
              {isCustomer && (
                <Card className={"label-large p-3"} disableHover={true}>
                  {canSeePrice && (
                    <div className={"mb-3 d-flex flex-justify-between"}>
                      <div>
                        <h2 className="scha__pdp__price-header">
                          {isLoadingPrice && <CircleSpinner />}
                          {!isLoadingPrice && !errorLoadingPrice && price && price.value > 0 && (
                            <>
                              <span>{PriceUtils.text(price)}</span>
                              <div className="label-large f-label-color f-normal">
                                {t("linearConfigSummary.pdp.perSystem")}
                              </div>
                            </>
                          )}
                          {!isLoadingPrice &&
                            !errorLoadingPrice &&
                            (price?.value ?? 0) === 0 &&
                            t("product.priceOnRequest")}
                          {!isLoadingPrice && errorLoadingPrice && !webservicesDisabled && (
                            <Alert severity={"error"}>{t("hybrisAjaxError")}</Alert>
                          )}
                        </h2>
                      </div>
                    </div>
                  )}

                  <PriceReferenceParam
                    product={productDescriptor.base}
                    className={"my-2"}
                    data-test-id={"pdp-price-reference"}
                    priceReference={priceReference}
                    editable={canSeePriceRefColumn()}
                    onChange={(value) => dispatch(updatePriceReference(value))}
                  />

                  <QuantityInput
                    min={0}
                    data-test-id={"pdp-input-quantity"}
                    max={MAX_QUANTITY}
                    value={quantity}
                    step={1}
                    onValueChange={(newVal) => dispatch(updateQuantity(newVal))}
                  />
                  <div className={"mb-2"}>
                    {isLatestKmatUser && (
                      <>
                        <div className={"d-flex flex-align-center mb-2"}>{leadTimeElement}</div>
                      </>
                    )}
                  </div>

                  {buttonsElement}
                </Card>
              )}
            </div>
            {isModalOpen && <LinearATCPopup onClose={openCloseModal} />}
            {isRequestQuoteOpen && <LinearRequestQuotePopup onClose={openCloseRequestQuote} />}
          </div>
        </div>
      </div>

      <DesignationDisclaimer />

      <Tabs
        id={"Tab"}
        fullWidthIndicator={false}
        variant={"fullWidth"}
        value={activeTab}
        onChange={(e, v) => {
          const val = v as LcSummaryTab;
          if (val === LcSummaryTab.CAD) {
            if (!forceShowCAD && isPublicUser) {
              redirectToUrl(
                getRedirectToRegisterPage({ targetPage: document.location.href + "?showCAD=true", openLogin: true }),
              );
              return;
            } else {
              trackCADTabClick();
              updateActiveTab(val);
            }
          } else {
            updateActiveTab(val);
          }
        }}
      >
        <Tab
          data-test-id={"linear-tab-information"}
          value={LcSummaryTab.ProductInformation}
          label={t("linearConfigSummary.tab.productInformation")}
        />
        <Tab data-test-id={"linear-tab-cad"} value={LcSummaryTab.CAD} label={t("linearConfigSummary.tab.cad.label")} />
      </Tabs>
      <LazyRenderPanel visible={activeTab === LcSummaryTab.ProductInformation}>
        <SummaryData isFullReview={true} />
      </LazyRenderPanel>
      <LazyRenderPanel visible={activeTab === LcSummaryTab.CAD}>
        <LinearCad />
      </LazyRenderPanel>
    </>
  );
};

const LazyRenderPanel = ({ visible, children }: { visible: boolean; children: ReactNode }) => {
  const [renderedOnce, setRenderedOnce] = useState(visible);
  useLayoutEffect(() => {
    if (visible && !renderedOnce) {
      setRenderedOnce(true);
    }
  }, [visible]);

  if (renderedOnce) {
    return (
      <div className={"scha__lc__summary--full-page"} hidden={!visible}>
        {children}
      </div>
    );
  }
  return null;
};

const getLinearConfiguration = (state: RootState) => state.linearConfigurator;
export const getSystemLayout = (state: RootState) => state.linearConfigurator.substeps.SystemLayout;
export const selectProductImageUrl = (state: RootState) => {
  if (
    state.linearConfigurator.type === ConfiguratorType.System &&
    state.linearConfigurator.productCode === UNIT_CONFIGURATOR_PRODUCT_CODE
  ) {
    const systemLayout = getSystemLayout(state);
    const tileSelectedInFirstStep = systemLayout.selectedTile;
    return systemLayout.tiles[tileSelectedInFirstStep!].imageUrl;
  }

  return state.linearConfigurator.productImageUrl;
};
const getQuantity = (state: RootState) => state.linearConfigurator.quantity;
const getManufacturerLogo = (state: RootState) => state.linearConfigurator.manufacturerLogo;

const RequestOfferButton: FC<{
  className?: string;
  isPublicUser: boolean;
  children?: ReactNode;
}> = ({ children, className, isPublicUser }) => {
  const { openModal, component } = useRequestOfferPopup();
  const trackClick = useTrackLCSimpleButtonClick("request_offer");

  const onClick = (e: React.MouseEvent) => {
    e.preventDefault();
    trackClick();
    if (!isPublicUser) {
      openModal();
    } else {
      redirectOrReload(REGISTER_PAGE);
    }
  };

  return (
    <>
      <Button
        className={className}
        href={isPublicUser ? REGISTER_PAGE : undefined}
        data-test-id={"linear-config-summary-request-offer-button"}
        fullWidth={true}
        onClick={onClick}
      >
        {children}
      </Button>

      {component}
    </>
  );
};

const SaveConfigurationButton: FC<{
  className?: string;
  isPublicUser: boolean;
  canImportSave: boolean;
  children?: ReactNode;
}> = ({ children, className, isPublicUser, canImportSave }) => {
  const { openModal, component } = useSaveConfigPopup();

  const onClick = () => {
    openModal();
  };

  return (
    <>
      <Button
        color={"secondary"}
        className={className}
        href={isPublicUser ? REGISTER_PAGE : undefined}
        data-test-id={"linear-config-summary-save-configuration-button"}
        fullWidth={true}
        onClick={!isPublicUser && canImportSave ? onClick : undefined}
      >
        {children}
      </Button>

      {component}
    </>
  );
};

export const selectDataComponent = (state: RootState) => {
  const productCode = state.linearConfigurator.productCode;
  return getConfiguratorDefinition(productCode).summary;
};

const selectConfiguratorType = (state: RootState) => {
  return state.linearConfigurator.type;
};
const selectConfiguratorProductCode = (state: RootState) => {
  return state.linearConfigurator.productCode;
};

export const selectedPriceReference = (state: RootState) => state.linearConfigurator.priceReference;
